import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Alert, Box, Typography } from '@mui/material';
import type { SafetyCaseRecord } from '../../services/reportsApi';
import ContributingConditionReviewSection, {
  type ContributingConditionPreview,
} from './ContributingConditionReviewSection';
import ClosureSummarySection from './ClosureSummarySection';
import FollowUpResultSection from './FollowUpResultSection';
import SafetyCaseAttestationSaveBar from './SafetyCaseAttestationSaveBar';
import {
  runSafetyCaseAttestationSave,
  type SafetyCaseAttestationSaveHandler,
} from '../../domain/safetyCases/safetyCaseAttestationSave';
import {
  resolveClaimReadinessSafetyCaseFocus,
  type ClaimReadinessSafetyCaseFocus,
} from '../../domain/incidents/riskPatternWorkflowReviewContext';

export type SafetyCaseAttestationPanelHandle = {
  focusBlocker: (controlId: string) => void;
};

type SafetyCaseAttestationPanelProps = {
  safetyCaseId: string;
  item: SafetyCaseRecord;
  readOnly?: boolean;
  workflowImpactSummary?: Record<string, unknown> | null;
  followupMeasurement?: Record<string, unknown> | null;
  onSaved?: () => void;
};

function readFollowupMeasurement(item: SafetyCaseRecord): Record<string, unknown> {
  return (
    ((item as { followup_measurement?: Record<string, unknown> }).followup_measurement)
    || item.status_snapshot?.followup_measurement
    || {}
  ) as Record<string, unknown>;
}

export const SafetyCaseAttestationPanel = forwardRef<
  SafetyCaseAttestationPanelHandle,
  SafetyCaseAttestationPanelProps
>(function SafetyCaseAttestationPanel(
  {
    safetyCaseId,
    item,
    readOnly = false,
    workflowImpactSummary,
    followupMeasurement,
    onSaved,
  },
  ref,
) {
  const contributingConditionReviewRef = useRef<HTMLDivElement | null>(null);
  const closureSummaryRef = useRef<HTMLDivElement | null>(null);
  const contributingConditionSaveRef = useRef<SafetyCaseAttestationSaveHandler | null>(null);
  const closureSummarySaveRef = useRef<SafetyCaseAttestationSaveHandler | null>(null);
  const followUpResultSaveRef = useRef<SafetyCaseAttestationSaveHandler | null>(null);
  const [attestationSaving, setAttestationSaving] = useState(false);
  const [attestationSaveError, setAttestationSaveError] = useState<string | null>(null);
  const [attestationSaveNotice, setAttestationSaveNotice] = useState<string | null>(null);
  const [, setContributingPreview] = useState<ContributingConditionPreview>({
    likelyContributingCondition: '',
    contributingConditionStatus: '',
  });

  const followup = followupMeasurement || readFollowupMeasurement(item);

  const focusBlocker = useCallback((controlId: string) => {
    const focus = resolveClaimReadinessSafetyCaseFocus(controlId) as ClaimReadinessSafetyCaseFocus | null;
    if (focus === 'contributing_condition_status_required') {
      contributingConditionReviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (focus === 'closure_summary_required') {
      closureSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useImperativeHandle(ref, () => ({ focusBlocker }), [focusBlocker]);

  const handleSaveAllAttestation = useCallback(async () => {
    setAttestationSaving(true);
    setAttestationSaveError(null);
    setAttestationSaveNotice(null);
    try {
      await runSafetyCaseAttestationSave([
        contributingConditionSaveRef.current,
        closureSummarySaveRef.current,
        followUpResultSaveRef.current,
      ]);
      setAttestationSaveNotice('Safety Case attestation saved. Claim readiness will refresh.');
      onSaved?.();
    } catch (err: unknown) {
      const message = err instanceof Error
        ? err.message
        : 'Unable to save Safety Case attestation right now.';
      setAttestationSaveError(message);
    } finally {
      setAttestationSaving(false);
    }
  }, [onSaved]);

  useEffect(() => {
    setAttestationSaveError(null);
    setAttestationSaveNotice(null);
  }, [item.updated_at, safetyCaseId]);

  return (
    <Box data-testid="safety-scan-case-attestation-panel">
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
        Safety Case attestation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Complete these fields here to clear sign-off blockers. You do not need to leave this Safety Scan page.
      </Typography>

      <Box ref={contributingConditionReviewRef}>
        <ContributingConditionReviewSection
          safetyCaseId={safetyCaseId}
          item={item}
          readOnly={readOnly}
          hideSaveButton
          saveHandlerRef={contributingConditionSaveRef}
          onPreviewChange={setContributingPreview}
        />
      </Box>

      <Box ref={closureSummaryRef}>
        <ClosureSummarySection
          safetyCaseId={safetyCaseId}
          item={item}
          readOnly={readOnly}
          hideSaveButton
          saveHandlerRef={closureSummarySaveRef}
          workflowImpactSummary={workflowImpactSummary}
        />
      </Box>

      <FollowUpResultSection
        safetyCaseId={safetyCaseId}
        item={item}
        readOnly={readOnly}
        hideSaveButton
        saveHandlerRef={followUpResultSaveRef}
        workflowImpactSummary={workflowImpactSummary}
        followupMeasurement={followup}
      />

      {!readOnly ? (
        <SafetyCaseAttestationSaveBar
          saving={attestationSaving}
          error={attestationSaveError}
          notice={attestationSaveNotice}
          onSave={() => { void handleSaveAllAttestation(); }}
        />
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          Read-only access. Safety Case attestation cannot be edited from this workspace.
        </Alert>
      )}
    </Box>
  );
});

export default SafetyCaseAttestationPanel;
